import React, { useState, useEffect, useContext, useMemo } from 'react';
import { GoogleMap, useLoadScript, Marker, DirectionsRenderer, DirectionsService } from '@react-google-maps/api';
import factory from '../../../../../assets/router/factory.svg'
import { RouterInfoContext } from '../../../../../contexts/RouterInfoContext';
import { MapWrapper } from '../style';
import axios from '../../../../../config/axios';

interface MapsProps {
  apiKey: string;
  center?: { lat: number; lng: number };
  zoom?: number;
  markers?: { lat: number; lng: number }[];
  wayPoints?: Waypoints[];
  companyDestination?: boolean;
}
interface Waypoints {
  location: string,
  stopover: boolean
}
const defaultCenter = {
  lat: -23.5505,
  lng: -46.6333
};

const polylineOptions = {
  strokeColor: '#0042f9',
  strokeWeight: 5,
  opacity: 0.8,

}

const markerOptions: google.maps.MarkerOptions = {
  label: {
    text: String(({ index }: { index: number }) => (index + 1)),
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
  },
};
const defaultZoom = 12;

const MyMap: React.FC<MapsProps> = ({
  apiKey,
  center = defaultCenter,
  zoom = defaultZoom,
  markers,
  wayPoints,
  companyDestination
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey || '',
  });

  const [directions, setDirections] = React.useState<google.maps.DirectionsResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [urlRoute, setUrlRoute] = useState<string | null>(null)
  const { routerInfo, setRouterInfo } = useContext(RouterInfoContext)
  const [track, setTrack] = useState<string | null>(null)
  const [originAddress, setOriginAddress] = useState<string>('');

  useEffect(() => {
  const fetchCompanyAddress = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies/me/details`);
      const data = response.data;
      const street = data.street || '';
      const number = data.number || '';
      const neighborhood = data.neighborhood?.name || data.neighborhood || '';
      const cep = data.cep || '';

      const city = (data.city?.name === 'Santa Fé' || !data.city?.name) ? 'Osasco' : data.city.name;
      const state = data.city?.state?.uf || data.state?.name || 'SP';

      const fullAddress = `${street}, ${number}${data.complement ? ` - ${data.complement}` : ''} - ${neighborhood}, ${city} - ${state}, ${cep}`;
      

      setOriginAddress(fullAddress);
    } catch (error: any) {
      console.error("❌ Erro ao carregar endereço de origem:", error);
      if (error?.response) {
        console.error("Detalhes do erro do servidor:", error.response.data);
      }
    }
  };

  fetchCompanyAddress();
}, []);

const mockDirection = useMemo(() => {
  const direction = {
    origin: originAddress || 'Carregando...', 
    destination: companyDestination ? originAddress : (wayPoints && wayPoints.length > 0 ? wayPoints[wayPoints.length - 1].location : originAddress),
    waypoints: companyDestination ? wayPoints : wayPoints?.slice(0, -1),
    travelMode: 'DRIVING' as google.maps.TravelMode
  };

  // Log 3: Monitoramento da Mudança de Rota
  if (originAddress) {
    console.log('🗺️ Rota calculada com Origem Dinâmica:', {
      origem: direction.origin,
      destino: direction.destination,
      totalParadas: wayPoints?.length || 0
    });
  }

  return direction;
}, [originAddress, wayPoints, companyDestination]);


  useEffect(() => {
    if (!isLoaded || !wayPoints || !mockDirection.origin || mockDirection.origin === 'Carregando...') return;
    const directionsService = new google.maps.DirectionsService();
    const fetchDirections = async () => {
      try {
        console.log('🚀 Calculando rota de:', mockDirection.origin);
        let waypointsString = '';
        directionsService.route(
          {
            origin: mockDirection.origin,
            destination: mockDirection.destination,
            waypoints: mockDirection.waypoints,
            travelMode: mockDirection.travelMode,
            optimizeWaypoints: false,
          },
          (result, status) => {
            if (status === google.maps.DirectionsStatus.OK && result) {
              setDirections(result);
              //Gera URL
              const waypointsLocations = mockDirection.waypoints?.map(waypoint => encodeURIComponent(waypoint.location));
              waypointsString = '&waypoints=' + waypointsLocations?.join('|');
              const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(mockDirection.origin)}&destination=${encodeURIComponent(mockDirection.destination)}&travelmode=driving${wayPoints.length > 0 ? waypointsString : ''}`;
              const totalDistanceInMeters = result.routes[0].legs.reduce((total, leg) => total + (leg.distance?.value || 0), 0);
              let formattedDistance = '';

              // Verifica se a distância é menor que 1000 metros
              if (totalDistanceInMeters < 1000) {
                // Se for, formata o valor em metros
                formattedDistance = `${totalDistanceInMeters.toFixed(0)} m`;
              } else {
                // Se não, converte para km e formata com duas casas decimais
                const totalDistanceInKm = (totalDistanceInMeters / 1000).toFixed(2);
                formattedDistance = `${totalDistanceInKm} km`;
              }
              //Calcula o tempo
              const trackTime = result.routes[0].legs.reduce((total, leg) => total + (leg.duration?.value || 0), 0);
              const seconds = trackTime;

              const days = Math.floor(seconds / (3600 * 24)); // 3600 segundos/hora * 24 horas/dia
              const remainingSecondsAfterDays = seconds % (3600 * 24);

              const hours = Math.floor(remainingSecondsAfterDays / 3600);
              const remainingSecondsAfterHours = remainingSecondsAfterDays % 3600;

              const minutes = Math.floor(remainingSecondsAfterHours / 60);
              // const remainingSeconds = remainingSecondsAfterHours % 60; // Você pode manter segundos se quiser, mas geralmente não é comum para rotas longas
              let trackString = '';

              if (days > 0) {
                trackString += `${days}d `;
              }
              if (hours > 0) {
                trackString += `${hours}h `;
              }
              if (minutes > 0) {
                trackString += `${minutes}min`;
              }

              // Caso a rota seja muito curta e resulte em 0d 0h 0min, podemos exibir "menos de 1 min"
              if (trackString.trim() === '') {
                trackString = 'menos de 1 min';
              } else {
                trackString = trackString.trim(); // Remove espaço extra no final se houver
              }

              setTrack(`${trackString} - ${formattedDistance}`)
              setUrlRoute(url)
            } else {
              console.error('Error fetching directions:', status);
              setError(`Failed to fetch directions: ${status}`);
              setDirections(null);
            }
          }

        );
      } catch (err) {
        console.error('Error in directions request:', err);
        setError('An unexpected error occurred while calculating the route');
        setDirections(null);
      }
    };

    fetchDirections();
  }, [isLoaded, wayPoints, mockDirection]);

  useEffect(() => {
    if (track !== null && urlRoute !== null) {

      const newUrl = urlRoute;
      const newTrack = track;

      if (routerInfo?.url !== newUrl || routerInfo?.track !== newTrack) {
        setRouterInfo({ url: newUrl, track: newTrack });
      }
    }
  }, [track, urlRoute, setRouterInfo]);

  if (loadError) {
    return (
      <div>
        <p>Error loading Google Maps: {loadError.message}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div >
        <p >Loading map...</p>
      </div>
    );
  }

  return (
    <MapWrapper>
      {/* {error && (
        <div>
          <p>{error}</p>
        </div>
      )} */}
      {/* <a href={String(urlRoute)}>Acessar maps</a>
      <p>{track}</p> */}
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={zoom}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {wayPoints && wayPoints.length > 0 && directions && <DirectionsRenderer directions={directions} options={{ polylineOptions: polylineOptions, suppressMarkers: false }} />}
      </GoogleMap>
    </MapWrapper>
  );
};

export default MyMap;